/**
 * BI refund settlement workspace — `/api/bi/payments/refunds/*`.
 *
 * Spine is `payment.Refund` (accounts). CRM approval attaches when present.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, get, post } from '@/api/client';
import { env } from '@/lib/env';

export type RefundStatus = 'pending' | 'completed' | 'cancelled';
export type RefundType = 'full' | 'partial';

export interface PaymentRefund {
  id: number;
  order_id: number | null;
  order_status: string | null;
  order_total: number;
  payment_type: string;
  gateway_reference: string;
  store: { id: number; name: string } | null;
  customer_name: string;
  refund_type: RefundType;
  refund_amount: number;
  status: RefundStatus;
  remarks: string;
  image: string | null;
  created_at: string | null;
  completed_at: string | null;
  settled_by: { id: number; name: string | null } | null;
  crm: {
    request_id: number;
    ticket_id: number;
    decision: string;
    requested_amount: number;
    approved_amount: number | null;
  } | null;
}

export interface EligibleOrder {
  order_id: number;
  status: string;
  payment_type: string;
  final_total: number;
  gateway_reference: string;
  store: { id: number; name: string } | null;
  customer_name: string;
  created_at: string | null;
}

export interface PaginatedRefunds {
  results: PaymentRefund[];
  pagination: { page: number; page_size: number; total: number; total_pages: number };
}

export interface PaginatedEligible {
  results: EligibleOrder[];
  pagination: { page: number; page_size: number; total: number; total_pages: number };
}

export interface RefundAnalytics {
  range: { start: string; end: string };
  pending: {
    count: number;
    value: number;
    aging: { '0_1d': number; '1_3d': number; '3_7d': number; '7d_plus': number };
  };
  settled: {
    count: number;
    value: number;
    median_create_to_settle_hours: number | null;
    avg_create_to_settle_hours: number | null;
    by_day: Array<{ day: string | null; count: number; value: number }>;
    by_store: Array<{ store_id: number | null; store_name: string; count: number; value: number }>;
    by_payment_type: Array<{ payment_type: string; count: number; value: number }>;
  };
  leakage: {
    approved_unsettled_count: number;
    approved_unsettled_value: number;
  };
}

function invalidateRefunds(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['bi', 'payments', 'refunds'] });
}

export function usePaymentRefunds(filters: {
  status?: RefundStatus | 'settled' | 'all' | null;
  paymentType?: string | null;
  storeId?: number | null;
  orderId?: number | null;
  q?: string | null;
  start?: string | null;
  end?: string | null;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: [
      'bi',
      'payments',
      'refunds',
      'list',
      filters.status ?? null,
      filters.paymentType ?? null,
      filters.storeId ?? null,
      filters.orderId ?? null,
      filters.q ?? null,
      filters.start ?? null,
      filters.end ?? null,
      filters.page ?? 1,
      filters.pageSize ?? 25,
    ],
    queryFn: () =>
      get<PaginatedRefunds>('/api/bi/payments/refunds/', {
        params: {
          page: filters.page ?? 1,
          page_size: filters.pageSize ?? 25,
          status:
            filters.status && filters.status !== 'all' ? filters.status : undefined,
          payment_type: filters.paymentType ?? undefined,
          store_id: filters.storeId ?? undefined,
          order_id: filters.orderId ?? undefined,
          q: filters.q ?? undefined,
          start: filters.start ?? undefined,
          end: filters.end ?? undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });
}

export function useEligibleRefundOrders(filters: {
  q?: string | null;
  paymentType?: string | null;
  storeId?: number | null;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      'bi',
      'payments',
      'refunds',
      'eligible',
      filters.q ?? null,
      filters.paymentType ?? null,
      filters.storeId ?? null,
      filters.page ?? 1,
      filters.pageSize ?? 10,
    ],
    queryFn: () =>
      get<PaginatedEligible>('/api/bi/payments/refunds/eligible/', {
        params: {
          page: filters.page ?? 1,
          page_size: filters.pageSize ?? 10,
          q: filters.q ?? undefined,
          payment_type: filters.paymentType ?? undefined,
          store_id: filters.storeId ?? undefined,
        },
      }),
    enabled: filters.enabled !== false,
    placeholderData: keepPreviousData,
  });
}

export function useRefundAnalytics(filters: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
}) {
  return useQuery({
    queryKey: [
      'bi',
      'payments',
      'refunds',
      'analytics',
      filters.start ?? null,
      filters.end ?? null,
      filters.storeId ?? null,
    ],
    queryFn: () =>
      get<RefundAnalytics>('/api/bi/payments/refunds/analytics/', {
        params: {
          start: filters.start ?? undefined,
          end: filters.end ?? undefined,
          store_id: filters.storeId ?? undefined,
        },
      }),
  });
}

export function useCreatePaymentRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      order_id: number;
      refund_amount: number | string;
      refund_type?: RefundType;
      remarks?: string;
    }) => post<PaymentRefund>('/api/bi/payments/refunds/', body),
    onSuccess: () => invalidateRefunds(qc),
  });
}

export function useSettlePaymentRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      id: number;
      action: 'completed' | 'cancelled';
      image?: File | null;
    }) => {
      const fd = new FormData();
      fd.append('action', body.action);
      if (body.image) fd.append('image', body.image);
      const r = await apiClient.post<PaymentRefund>(
        `/api/bi/payments/refunds/${body.id}/settle/`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return r.data;
    },
    onSuccess: () => invalidateRefunds(qc),
  });
}

/** Build a media URL for a refund screenshot path returned by the API. */
export function refundImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  const cleaned = path.replace(/^\/+/, '');
  // Paths are stored as `src/images/refund_screenshots/...` under MEDIA_ROOT.
  return `${env.apiBase}/media/${cleaned}`;
}
